import i18n from '../../i18n'
import { backendClient } from '../../api/backend'
import { clearStore } from '../store'
import { clearDocument } from './documentAction'
import { clearSearchDocument } from './searchAction'
import { fetchSubscription, clearSubscription } from './subscriptionAction'
import { fetchPricing } from './pricingActions'
import { tracker } from '../../analytics'
import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGOUT,
  FORGOT_PASSWORD_REQUEST,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_FAILURE,
  SIGNUP_FAILURE,
  SIGNUP_SUCCESS,
  SIGNUP_REQUEST,
  CLEAR_AUTH_STATE,
  CLEAR_MESSAGE_ERROR,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_FAILURE,
  SET_IS_BOARDED,
  FETCH_USER,
  REQUEST_OTP,
  REQUEST_OTP_SUCCESS,
  REQUEST_OTP_FAILURE,
  VERIFY_OTP,
  VERIFY_OTP_SUCCESS,
  VERIFY_OTP_FAILURE,
  RESEND_OTP,
  RESEND_OTP_SUCCESS,
  RESEND_OTP_FAILURE
} from './types'

export const updateIsBoarded = (user_id, isBoarded) => dispatch => {
  backendClient.updateBoarded(user_id, isBoarded)

  dispatch({
    type: SET_IS_BOARDED,
    payload: isBoarded
  })
}

// Login
export const loginRequest = () => ({
  type: LOGIN_REQUEST
})

export const loginSuccess = user => ({
  type: LOGIN_SUCCESS,
  payload: user
})

export const loginFailure = error => ({
  type: LOGIN_FAILURE,
  payload: error
})

export const fetchUser = userId => async dispatch => {
  try {
    const data = await backendClient.fetchUser(userId)
    // backendClient.fetchUser swallows network errors and returns undefined.
    // Real auth failures (401/403) already dispatch logout() in the client, so
    // keep the current session instead of wiping the user on a transient blip.
    if (!data) return

    dispatch({
      type: FETCH_USER,
      payload: data
    })
    // Identify user for analytics
    tracker.identify(data.id, data.organization_id)
    // Load subscription and pricing in parallel after user is resolved (e.g. on page refresh)
    dispatch(fetchSubscription())
    dispatch(fetchPricing())
  } catch (error) {
    console.log('fetchUser Error', error)
  }
}

export const login = (email, password, navigate) => {
  return async dispatch => {
    dispatch(loginRequest())
    try {
      // Call backend login endpoint
      const response = await backendClient.login(email, password)
      
      // Check if response indicates OTP is required
      if (response.requires_otp) {
        // Navigate to OTP screen for new device
        navigate(`/verify-otp?email=${encodeURIComponent(email)}`)
        
        // Request OTP in the background
        dispatch(requestLoginOTP(email, navigate))
      } else {
        // Direct login successful (existing device)
        dispatch(loginSuccess(response))
        dispatch(fetchSubscription())
        dispatch(fetchPricing())

        // Navigate to main app
        setTimeout(() => {
          navigate('/')
        }, 100)
      }
    } catch (error) {
      let errorMessage = i18n.t('auth_error_unexpected');

      // If we have detailed error data from backend (already translated by backend)
      if (error.data && error.data.detail) {
        const detail = error.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // FastAPI 422 validation errors: [{type, loc, msg, input, url}, ...]
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      dispatch(loginFailure(errorMessage))
    }
  }
}

export const loginWithToken = (token, navigate) => {
  return async dispatch => {
    dispatch(loginRequest());
    try {
      const response = await backendClient.post('api/auth/confirm', { token });

      // Check if response is ok before parsing JSON
      if (!response.ok) {
        const errorData = await response.json();
        const errorStatus = response.status;

        // Handle different error types based on status codes
        switch (errorStatus) {
          case 400:
            throw new Error(errorData.detail || i18n.t('auth_error_token_invalid'));
          case 403:
            throw new Error(errorData.detail || i18n.t('auth_error_org_no_subscription'));
          case 404:
            throw new Error(errorData.detail || i18n.t('auth_error_user_not_found'));
          default:
            throw new Error(errorData.detail || i18n.t('auth_error_confirmation_failed'));
        }
      }

      const data = await response.json();

      // Save user data to local storage
      localStorage.setItem('access_token', data.token.access_token);
      localStorage.setItem('token_type', data.token.token_type);
      // Store the current token version for this session
      if (data.user.active_tokens && data.user.active_tokens.length > 0) {
        const currentToken = data.user.active_tokens[data.user.active_tokens.length - 1];
        localStorage.setItem('token_version', currentToken.version);
      }

      // Update auth state
      dispatch(loginSuccess(data));
      dispatch(fetchSubscription());
      dispatch(fetchPricing());

      // Check for a pending Stripe checkout (set during signup from landing page)
      const pendingCheckout = localStorage.getItem('pendingCheckout')
      if (pendingCheckout) {
        const { plan, billing, region, lang: checkoutLang, quantity, currency: checkoutCurrency } = JSON.parse(pendingCheckout)
        // Don't remove pendingCheckout here — keep it so /payment-cancel can retry.
        // It gets cleaned up on /payment-success.

        // Set app language from landing page preference (fire-and-forget)
        if (checkoutLang && ['en', 'fr', 'ar'].includes(checkoutLang)) {
          backendClient.updateAppPreferences({ app_language: checkoutLang, search_language: checkoutLang }).catch(() => {})
          i18n.changeLanguage(checkoutLang)
        }

        try {
          const sessionData = await backendClient.createStripeSession(plan, billing, null, region, checkoutLang || 'auto', quantity || 1, checkoutCurrency || null)
          window.location.href = sessionData.session_url
          return data
        } catch (stripeError) {
          // Org already has an active subscription — navigate to app, SubscriptionRoute will allow access
          if (stripeError?.message?.includes('already has an active subscription')) {
            navigate('/')
            return data
          }
          // Any other Stripe error — log the user out so they cannot access the app without paying
          localStorage.removeItem('access_token')
          localStorage.removeItem('token_type')
          localStorage.removeItem('token_version')
          dispatch(loginFailure(stripeError.message || i18n.t('auth_error_unexpected')))
          throw stripeError
        }
      }

      // Default: navigate to app
      setTimeout(() => {
        navigate('/');
      }, 2000);

      return data;
    } catch (error) {

      // The error message is already translated from the backend or set above
      const errorMessage = error.message || i18n.t('auth_error_confirmation_failed');

      // Dispatch login failure with appropriate error message
      dispatch(loginFailure(errorMessage));

      // Re-throw for component-level handling
      throw errorMessage;
    }
  };
}

// Sign Up
export const signUpRequest = () => ({
  type: SIGNUP_REQUEST
})

export const signUpSuccess = user => ({
  type: SIGNUP_SUCCESS,
  payload: user
})

export const signUpFailure = error => ({
  type: SIGNUP_FAILURE,
  payload: error
})

export const updateUserProfile = (profileData, navigate) => async dispatch => {
  console.log('[updateUserProfile] called, redirect_after_onboarding=', localStorage.getItem('redirect_after_onboarding'))
  const data = await backendClient.updateProfile(profileData)
  console.log('[updateUserProfile] profile updated, dispatching is_boarded=true')
  dispatch({
    type: 'UPDATE_USER_PROFILE_SUCCESS',
    payload: { ...profileData, is_boarded: true }
  })
  console.log('[updateUserProfile] dispatched, redirect_after_onboarding=', localStorage.getItem('redirect_after_onboarding'))
  // Post-onboarding navigation is handled by OnboardingGuard in AppRouter.js.
  // When is_boarded flips to true, the guard redirects to:
  // - /contract/{id} if localStorage('redirect_after_onboarding') is set (external collab)
  // - / (dashboard) otherwise
  return data
}

export const updateUserProfileSettings = (profileData) => async dispatch => {
  const data = await backendClient.updateProfile(profileData)
  dispatch({
    type: 'UPDATE_USER_PROFILE_SUCCESS',
    payload: { ...profileData, is_boarded: true }
  })
  return data
}

export const signUp = (
  email,
  password,
  navigate
) => {
  return async dispatch => {
    dispatch(signUpRequest())

    try {
      // Read plan preferences from localStorage (set by signup screen from URL params)
      const selectedPlanRaw = localStorage.getItem('selectedPlan')
      const selectedPlan = selectedPlanRaw ? JSON.parse(selectedPlanRaw) : null

      // Pass plan info to backend so trial subscription gets the right plan
      const planPrefs = selectedPlan ? {
        plan: selectedPlan.plan,
        billing_cycle: selectedPlan.billing,
        region: selectedPlan.region,
        quantity: selectedPlan.quantity,
      } : {}

      const data = await backendClient.signUp(email, password, planPrefs)
      tracker.conversion({ category: 'signup_success', action: 'account_created', properties: { email } })

      if (selectedPlan?.lang && ['en', 'fr', 'ar'].includes(selectedPlan.lang)) {
        i18n.changeLanguage(selectedPlan.lang)
      }

      // OTP required — navigate to verification screen before granting access
      if (data.requires_otp) {
        // Store token temporarily so verify-otp can reuse the device
        localStorage.setItem('access_token', data.token.access_token)
        localStorage.setItem('token_type', data.token.token_type)
        if (data.token.token_version) {
          localStorage.setItem('token_version', data.token.token_version)
        }
        // Set language preference (fire-and-forget, token is available)
        if (selectedPlan?.lang && ['en', 'fr', 'ar'].includes(selectedPlan.lang)) {
          backendClient.updateAppPreferences({ app_language: selectedPlan.lang, search_language: selectedPlan.lang }).catch(() => {})
        }
        dispatch(requestOTPSuccess({ email }))
        navigate(`/verify-otp?email=${encodeURIComponent(email)}`)
        return
      }

      // No OTP required (OTP disabled) — log in directly
      localStorage.setItem('access_token', data.token.access_token)
      localStorage.setItem('token_type', data.token.token_type)
      if (data.token.token_version) {
        localStorage.setItem('token_version', data.token.token_version)
      }
      if (selectedPlan?.lang && ['en', 'fr', 'ar'].includes(selectedPlan.lang)) {
        backendClient.updateAppPreferences({ app_language: selectedPlan.lang, search_language: selectedPlan.lang }).catch(() => {})
      }
      dispatch(loginSuccess(data))
      dispatch(fetchSubscription())
      dispatch(fetchPricing())
      navigate('/onboarding')
    } catch (error) {
      console.log('Error in signUp action:', error)

      let errorMessage = i18n.t('auth_error_unexpected')

      if (error.status === 422) {
        // Pydantic validation error — show password requirements
        errorMessage = i18n.t('auth_password_requirements')
      } else if (error.data && error.data.detail) {
        errorMessage = typeof error.data.detail === 'string' ? error.data.detail : i18n.t('auth_error_invalid_signup')
      } else if (error.status === 409) {
        errorMessage = i18n.t('auth_error_user_exists')
      } else if (error.status === 400) {
        errorMessage = i18n.t('auth_error_invalid_signup')
      } else if (error.status === 500) {
        errorMessage = i18n.t('auth_error_server')
      } else if (error.status === 0) {
        errorMessage = i18n.t('auth_error_no_connection')
      } else if (error.message) {
        errorMessage = error.message
      }

      dispatch(signUpFailure(errorMessage))
    }
  }
}

// Sign Out
export const logout = () => {
  return dispatch => {
    tracker.reset()
    dispatch(clearSubscription())
    dispatch(clearAuthState())
    clearStore()
    dispatch({ type: LOGOUT })
  }
}

// Forgot Password
export const forgotPasswordRequest = () => ({
  type: FORGOT_PASSWORD_REQUEST
})

export const forgotPasswordSuccess = message => ({
  type: FORGOT_PASSWORD_SUCCESS,
  payload: message
})

export const forgotPasswordFailure = error => ({
  type: FORGOT_PASSWORD_FAILURE,
  payload: error
})

export const forgotPassword = (email) => {
  return async dispatch => {
    dispatch(forgotPasswordRequest())
    try {
      const res = await backendClient.forgotPassword(email)

      // Success case - no need for additional parsing if already handled in backendClient
      dispatch(
        forgotPasswordSuccess(i18n.t('auth_success_password_reset_sent'))
      )
    } catch (error) {
      let errorMessage = i18n.t('auth_error_unexpected');

      if (error.data && error.data.detail) {
        errorMessage = typeof error.data.detail === 'string'
          ? error.data.detail
          : Array.isArray(error.data.detail)
            ? error.data.detail.map(e => e.msg).join(', ')
            : i18n.t('auth_error_unexpected');
      } else if (error.status === 404) {
        errorMessage = i18n.t('auth_error_user_not_found');
      } else if (error.status === 401) {
        errorMessage = i18n.t('auth_error_forgot_unauthorized');
      } else if (error.status === 403) {
        errorMessage = i18n.t('auth_error_forgot_forbidden');
      } else if (error.status === 429) {
        errorMessage = i18n.t('auth_error_too_many_attempts');
      } else if (error.status === 500 || error.status === 502 || error.status === 503) {
        errorMessage = i18n.t('auth_error_server');
      } else if (error.message) {
        errorMessage = typeof error.message === 'string' ? error.message : i18n.t('auth_error_unexpected');
      }

      dispatch(forgotPasswordFailure(errorMessage))
    }
  }
}

// Reset Password
export const resetPassword = (token, newPassword) => {
  return async dispatch => {
    dispatch({ type: RESET_PASSWORD_REQUEST })
    try {
      const res = await backendClient.resetPassword(token, newPassword)
      dispatch({
        type: RESET_PASSWORD_SUCCESS,
        payload: i18n.t('auth_success_password_reset')
      })
    } catch (error) {
      let errorMessage = i18n.t('auth_error_unexpected')
      if (error.status === 422) {
        errorMessage = i18n.t('auth_password_requirements')
      } else if (error.data && error.data.detail) {
        errorMessage = typeof error.data.detail === 'string' ? error.data.detail : i18n.t('auth_error_unexpected')
      } else if (error.message) {
        errorMessage = error.message
      }
      dispatch({ type: RESET_PASSWORD_FAILURE, payload: errorMessage })
    }
  }
}

export const clearAuthState = () => ({
  type: CLEAR_AUTH_STATE
})
export const clearMessageError = () => ({
  type: CLEAR_MESSAGE_ERROR
})

// OTP Actions
export const requestOTP = () => ({
  type: REQUEST_OTP
})

export const requestOTPSuccess = data => ({
  type: REQUEST_OTP_SUCCESS,
  payload: data
})

export const requestOTPFailure = error => ({
  type: REQUEST_OTP_FAILURE,
  payload: error
})

export const verifyOTP = () => ({
  type: VERIFY_OTP
})

export const verifyOTPSuccess = data => ({
  type: VERIFY_OTP_SUCCESS,
  payload: data
})

export const verifyOTPFailure = error => ({
  type: VERIFY_OTP_FAILURE,
  payload: error
})

export const resendOTP = () => ({
  type: RESEND_OTP
})

export const resendOTPSuccess = data => ({
  type: RESEND_OTP_SUCCESS,
  payload: data
})

export const resendOTPFailure = error => ({
  type: RESEND_OTP_FAILURE,
  payload: error
})

// Request Email OTP
export const requestLoginOTP = (email, navigate) => {
  return async dispatch => {
    dispatch(requestOTP())
    try {
      const data = await backendClient.requestLoginOTP(email)
      dispatch(requestOTPSuccess(data))
    } catch (error) {
      let errorMessage = i18n.t('auth_error_otp_send');

      if (error.status === 404) {
        errorMessage = i18n.t('auth_error_user_not_found');
      } else if (error.status === 429) {
        errorMessage = i18n.t('auth_error_too_many_attempts');
      } else if (error.status === 500 || error.status === 502 || error.status === 503) {
        errorMessage = i18n.t('auth_error_server');
      } else if (!error.status) {
        errorMessage = i18n.t('auth_error_no_connection');
      }

      dispatch(requestOTPFailure(errorMessage))
    }
  }
}

export const verifyLoginOTP = (email, otp, navigate) => {
  return async dispatch => {
    dispatch(verifyOTP())

    try {
      // Call to verify OTP
      const data = await backendClient.verifyLoginOTP(email, otp)

      // Check if we have token in response
      if (!data.token) {
        throw new Error("No token returned")
      }

      // Store token with CONSISTENT key names
      localStorage.setItem('access_token', data.token.access_token); // Match this with getToken method
      localStorage.setItem('token_type', data.token.token_type);

      // Handle token version - look for it either directly in token object or from active_tokens
      let tokenVersion = data.token.token_version;

      // If token_version is not directly available, try to get from the latest active token
      if (!tokenVersion && data.user && data.user.active_tokens && data.user.active_tokens.length > 0) {
        const latestToken = data.user.active_tokens[data.user.active_tokens.length - 1];
        tokenVersion = latestToken.version;
      }

      // Store token version if available
      if (tokenVersion) {
        localStorage.setItem('token_version', tokenVersion);
      }

      // Dispatch success actions
      dispatch(verifyOTPSuccess(data))
      dispatch(fetchSubscription())
      dispatch(fetchPricing())

      // Delay navigation to allow state to update
      setTimeout(() => {
        // Check if user needs onboarding (e.g., external collaboration signup)
        const userData = data?.user || data?.token?.user
        if (userData && userData.is_boarded === false) {
          navigate('/onboarding')
        } else {
          // Check for pending external collaboration redirect
          const redirectContractId = localStorage.getItem('redirect_after_onboarding')
          if (redirectContractId) {
            localStorage.removeItem('redirect_after_onboarding')
            navigate(`/contract/${redirectContractId}`)
          } else {
            navigate('/')
          }
        }
      }, 100)

    } catch (error) {
      let errorMessage = i18n.t('auth_error_otp_verify_failed')

      if (error.status === 401 || error.status === 400) {
        errorMessage = i18n.t('auth_error_otp_invalid')
      } else if (error.status === 404) {
        errorMessage = i18n.t('auth_error_user_not_found')
      } else if (error.status === 429) {
        errorMessage = i18n.t('auth_error_too_many_attempts')
      } else if (error.status === 500 || error.status === 502 || error.status === 503) {
        errorMessage = i18n.t('auth_error_server')
      } else if (!error.status) {
        errorMessage = i18n.t('auth_error_no_connection')
      }

      dispatch(verifyOTPFailure(errorMessage))
    }
  }
}

// Resend OTP
export const resendLoginOTP = (email) => {
  return async dispatch => {
    dispatch(resendOTP())
    const data = await backendClient.resendLoginOTP(email)
    dispatch(resendOTPSuccess(data))
  }
}
